import apiClient from "../helpers/apiClient";
import { format, isValid, parseISO } from "date-fns";

export const employeeTypeService = {
  getEmployeeTypes,
  getEmployeeType,
  createEmployeeType,
  updateEmployeeType,
  deleteEmployeeType,
  setEmployeeTypeStatus,
};

function displayValue(value) {
  return value === null || value === undefined || value === "" ? "-" : value;
}

function formatEmployeeTypeDate(value) {
  if (value === null || value === undefined || value === "") return "-";

  const date =
    value instanceof Date
      ? value
      : typeof value === "string"
        ? parseISO(value)
        : new Date(value);

  return isValid(date) ? format(date, "dd MMM yyyy") : "-";
}

function findEmployeeTypePayload(payload) {
  if (Array.isArray(payload)) return payload;
  if (!payload || typeof payload !== "object") return [];

  const wrapped = payload.Employee_type_info ?? payload.employee_type_info;
  if (Array.isArray(wrapped)) return wrapped;
  if (wrapped && typeof wrapped === "object") return [wrapped];
  if (payload.employee_type_id !== undefined) return [payload];
  return [];
}

function mapEmployeeType(raw = {}) {
  return {
    id: displayValue(raw.employee_type_id),
    name: displayValue(raw.employee_type_name),
    statusId: displayValue(raw.employee_type_status_id),
    statusName: displayValue(raw.employee_type_status_name),
    createdBy: displayValue(raw.encoded_by_name),
    createdAt: formatEmployeeTypeDate(raw.encoded_date),
    lastChangedBy: displayValue(raw.last_changed_by_name),
    lastChangedAt: formatEmployeeTypeDate(raw.last_changed_date),
  };
}

function getErrorMessage(error) {
  return error.response?.data?.msg || "Something went wrong";
}

function extractWriteResult(response) {
  const data = response?.data;
  const messageObject = Array.isArray(data?.result_status)
    ? data.result_status[0]
    : Array.isArray(data)
      ? data[0]
      : data;

  return {
    message:
      messageObject?.message_text ||
      messageObject?.Message_Text ||
      "Saved successfully.",
    refNo: messageObject?.message_ref_no ?? messageObject?.Message_Ref_No ?? null,
  };
}

async function handleRequest(request) {
  try {
    return await request();
  } catch (error) {
    throw new Error(getErrorMessage(error));
  }
}

async function handleWriteRequest(request) {
  try {
    const response = await request();
    return extractWriteResult(response);
  } catch (error) {
    throw new Error(getErrorMessage(error));
  }
}

function getEmployeeTypes() {
  return handleRequest(async () => {
    const response = await apiClient.get("/hr/employee-types");
    return findEmployeeTypePayload(response?.data).map(mapEmployeeType);
  });
}

function getEmployeeType(id) {
  return handleRequest(async () => {
    const response = await apiClient.get(`/hr/employee-types/${id}`);
    const [employeeType] = findEmployeeTypePayload(response?.data);
    return employeeType ? mapEmployeeType(employeeType) : null;
  });
}

function createEmployeeType(name) {
  return handleWriteRequest(() =>
    apiClient.post("/hr/employee-types", { employee_type_name: name })
  );
}

function updateEmployeeType(id, name, statusId) {
  return handleWriteRequest(() =>
    apiClient.put(`/hr/employee-types/${id}`, {
      employee_type_name: name,
      employee_type_status_id: statusId,
    })
  );
}

function deleteEmployeeType(id) {
  return handleWriteRequest(() => apiClient.delete(`/hr/employee-types/${id}`));
}

function setEmployeeTypeStatus(id, statusId) {
  return handleWriteRequest(() =>
    apiClient.patch(`/hr/employee-types/${id}/status`, {
      employee_type_status_id: statusId,
    })
  );
}
